// src/utils/api.js

import axios from 'axios';
import { searchAyahsLocal } from './clientSearch';
import {
  loadPageLines,
  loadParahs,
  loadRukus,
  loadSajdas,
  loadSurahMerged,
  loadSurahsIndex,
  loadWordsForAyah,
} from './staticQuranData';

/**
 * Gets the base URL for the API based on the current environment.
 *
 * @returns {string} The base URL for the API.
 */
const getBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // If running on localhost, use the localhost URL
    return 'http://localhost:3001/api'; 
  } else {
    // Otherwise, extract the IP address and port from the current URL
    const currentUrl = new URL(window.location.href); 
    return `http://${currentUrl.hostname}:3001/api`; 
  }
};

// Create an Axios instance with the dynamically determined base URL
const axiosInstance = axios.create({
  baseURL: getBaseUrl(), 
});

/**
 * Makes a GET request to the specified API endpoint.
 *
 * @param {string} endpoint - The API endpoint to make the request to.
 * @param {object} [params={}] - Optional parameters to include in the request.
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. from AbortController).
 * @returns {Promise<object>} A promise that resolves with the response data.
 * @throws {Error} If there is an error making the request.
 */
export const axiosGet = async (endpoint, params = {}, signal) => {
  // Prevent ?params[surah]=...
  if (params && Object.prototype.hasOwnProperty.call(params, 'params')) {
    // Dev-only aid; avoid breaking prod.
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('Pass flat params to axiosGet, not {params:{...}}');
    } else {
      params = params.params; // fallback for prod if it ever happens
    }
  }

  const sig =
    signal && typeof signal.abort === 'function' ? signal : undefined;

  // Static JSON (public/surah-data/) — same response shapes as former /api routes
  if (endpoint === '/surahs') {
    try {
      const data = await loadSurahsIndex(sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static surahs index:', data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /surahs):', error);
      throw error;
    }
  }

  const surahMatch = /^\/surah\/(\d+)$/.exec(endpoint);
  if (surahMatch) {
    try {
      const data = await loadSurahMerged(surahMatch[1], sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static surah:', data);
      }
      return data;
    } catch (error) {
      console.error(`Error in axiosGet (static /surah/${surahMatch[1]}):`, error);
      throw error;
    }
  }

  if (endpoint === '/surah-rukus') {
    const surah = params && params.surah;
    if (surah == null || surah === '') {
      throw new Error('axiosGet /surah-rukus requires params.surah');
    }
    try {
      const data = await loadRukus(surah, sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static rukus:', data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /surah-rukus):', error);
      throw error;
    }
  }

  if (endpoint === '/words') {
    const surah = params && params.surah;
    const ayah = params && params.ayah;
    if (surah == null || surah === '' || ayah == null || ayah === '') {
      throw new Error('axiosGet /words requires params.surah and params.ayah');
    }
    try {
      const data = await loadWordsForAyah(surah, ayah, sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static words:', data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /words):', error);
      throw error;
    }
  }

  if (endpoint === '/parahs') {
    try {
      const data = await loadParahs(sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static parahs:', data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /parahs):', error);
      throw error;
    }
  }

  if (endpoint === '/sajdas') {
    try {
      const data = await loadSajdas(sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static sajdas:', data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /sajdas):', error);
      throw error;
    }
  }

  if (endpoint === '/pages') {
    const page =
      params && params.page != null && params.page !== ''
        ? Number(params.page)
        : 1;
    try {
      const data = await loadPageLines(page, sig);
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static pages:', page, data);
      }
      return data;
    } catch (error) {
      console.error('Error in axiosGet (static /pages):', error);
      throw error;
    }
  }

  if (endpoint === '/search') {
    const q =
      params && params.query != null && params.query !== ''
        ? String(params.query)
        : '';
    try {
      const data = await searchAyahsLocal(q, {
        signal: sig,
        langs: params.langs,
        scope: params.scope,
        limit: params.limit,
        offset: params.offset,
        preferredLangs: params.preferredLangs,
      });
      if (process.env.REACT_APP_DEBUG_CACHE === '1') {
        console.log('Static search:', q, data?.total);
      }
      // Keep array shape for any legacy callers; prefer .results when present.
      return Array.isArray(data) ? data : data?.results || [];
    } catch (error) {
      console.error('Error in axiosGet (static /search):', error);
      throw error;
    }
  }

  try {
    const requestConfig = {};
    if (params && Object.keys(params).length > 0) {
      requestConfig.params = params;
    }
    if (sig) {
      requestConfig.signal = sig;
    }
    const response = await axiosInstance.get(
      endpoint,
      Object.keys(requestConfig).length > 0 ? requestConfig : undefined
    );
    if (process.env.REACT_APP_DEBUG_CACHE === '1') {
      console.log('API Response:', response.data);
    }
    return response.data;
  } catch (error) {
    console.error(`Error in axiosGet for endpoint ${endpoint}:`, error);
    throw error; // Re-throw the error to be handled by the calling function
  }
};

/**
 * Base URL for Quran.com API
 */
const QURAN_API_BASE_URL = 'https://api.quran.com/api/v4';

// Create an Axios instance for Quran.com API
const quranApiInstance = axios.create({
  baseURL: QURAN_API_BASE_URL,
});

/**
 * Makes a GET request to the Quran.com API endpoint.
 *
 * @param {string} endpoint - The Quran.com API endpoint to make the request to.
 * @param {object} [params={}] - Optional parameters to include in the request.
 * @returns {Promise<object>} A promise that resolves with the response data.
 * @throws {Error} If there is an error making the request.
 */
export const axiosGetQuranApi = async (endpoint, params = {}) => {
  try {
    const response = await quranApiInstance.get(endpoint, { params });
    return response.data;
  } catch (error) {
    console.error(`Error in axiosGetQuranApi for endpoint ${endpoint}:`, error);
    throw error;
  }
};
